<script>
/**
 * 인출하기 — 상품구매 결제·인출 예약 건만 표시 (호가/P2P 거래 제외).
 * API: GET /api/v1/product-withdrawal
 */
import { getProductWithdrawalList } from '@/api/productWithdrawal.js'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'OrderStatusPage',
  components: { AppPagination },
  data() {
    const toDate = (d) => d.toISOString().substring(0, 10)
    const today = new Date()
    const defaultStart = new Date(today)
    defaultStart.setDate(defaultStart.getDate() - 15)
    const defaultEnd = new Date(today)
    defaultEnd.setMonth(defaultEnd.getMonth() + 1)
    return {
      search: {
        keyword: '',
        spotType: '',
        startDate: toDate(defaultStart),
        endDate: toDate(defaultEnd),
      },
      spotTypeOptions: [
        { title: '전체', value: '' },
        { title: '금', value: 'GOLD' },
        { title: '은', value: 'SILVER' },
      ],
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      summaryRows: [],
    }
  },
  watch: {
    '$route.query': {
      handler() {
        this.applySearchFromRoute()
        this.fetchList()
      },
      deep: true,
    },
  },
  mounted() {
    this.applySearchFromRoute()
    this.fetchList()
  },
  methods: {
    applySearchFromRoute() {
      const q = this.$route.query || {}
      if (q.keyword !== undefined && q.keyword !== null) this.search.keyword = String(q.keyword)
      if (q.spotType !== undefined && q.spotType !== null) this.search.spotType = String(q.spotType)
      if (q.startDate !== undefined && q.startDate !== null) this.search.startDate = String(q.startDate)
      if (q.endDate !== undefined && q.endDate !== null) this.search.endDate = String(q.endDate)
      const p = parseInt(String(q.page || ''), 10)
      if (!Number.isNaN(p) && p > 0) this.page = p
      const s = parseInt(String(q.size || ''), 10)
      if (!Number.isNaN(s) && s > 0) this.size = s
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getProductWithdrawalList({
          keyword: this.search.keyword || undefined,
          spotType: this.search.spotType || undefined,
          startDate: this.search.startDate || undefined,
          endDate: this.search.endDate || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          const data = res.data.data
          this.items = data?.items || []
          this.total = data?.total ?? 0
        }
      } catch (e) {
        console.error('[OrderStatusPage] fetchList', e)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      const toDate = (d) => d.toISOString().substring(0, 10)
      const today = new Date()
      const defaultStart = new Date(today)
      defaultStart.setDate(defaultStart.getDate() - 15)
      const defaultEnd = new Date(today)
      defaultEnd.setMonth(defaultEnd.getMonth() + 1)
      this.search = {
        keyword: '',
        spotType: '',
        startDate: toDate(defaultStart),
        endDate: toDate(defaultEnd),
      }
      this.page = 1
      this.fetchList()
    },
    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },
    formatDate(val) {
      if (!val) return '-'
      return String(val).substring(0, 10)
    },
    formatDateTime(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },
    withdrawnLabel(yn) {
      return yn === 'Y' ? '인출완료' : '미인출'
    },
    goToDetail(item) {
      if (!item?.paymentCode) return
      this.$router.push(`/order-status/${item.paymentCode}`)
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">인출하기</div>
        <div class="text-body-2 text-grey mt-1">
          상품구매에 따른 인출·예약 건만 조회합니다. 호가 매수·매도는 좌측 「매수·매도」 메뉴를 이용합니다.
        </div>
      </div>
    </div>

    <!-- 검색 영역 -->
    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="search.startDate"
              label="시작일"
              type="date"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="search.endDate"
              label="종료일"
              type="date"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="search.spotType"
              :items="spotTypeOptions"
              item-title="title"
              item-value="value"
              label="현물"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="search.keyword"
              label="키워드"
              placeholder="회원명, 결제코드, 상품명"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              @keyup.enter="handleSearch"
            />
          </v-col>
        </v-row>
        <div class="d-flex justify-end ga-2 mt-4 border-t-thin pt-4">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 목록 카드 -->
    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          상품 인출 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>

      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th class="text-center w-1/12">번호</th>
            <th class="text-center">결제코드</th>
            <th class="text-center">회원</th>
            <th class="text-center">상품</th>
            <th class="text-center">현물</th>
            <th class="text-center">중량(g)</th>
            <th class="text-right">결제금액</th>
            <th class="text-center">인출지점</th>
            <th class="text-center">예약일</th>
            <th class="text-center">인출</th>
            <th class="text-center">결제일시</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="item.paymentCode || index"
            class="withdrawal-row"
            style="cursor: pointer;"
            @click="goToDetail(item)"
          >
            <!-- 번호: 전체 건수 기준 역순 -->
            <td class="text-center text-caption text-grey">
              {{ total - (page - 1) * size - index }}
            </td>
            <td class="text-center text-caption text-grey-darken-1">{{ item.paymentCode }}</td>
            <td class="text-center font-weight-medium">
              {{ item.userName || '-' }}
              <span class="text-caption text-grey d-block">{{ item.userCode }}</span>
            </td>
            <td class="text-center text-body-2">{{ item.productName || '-' }}</td>
            <td class="text-center text-body-2">{{ item.spotTypeLabel || '-' }}</td>
            <td class="text-center text-caption">{{ item.weightG != null ? item.weightG : '-' }}</td>
            <td class="text-right text-caption font-weight-medium">
              {{ item.price != null ? item.price.toLocaleString() : '-' }}
            </td>
            <td class="text-center text-body-2">{{ item.branchName || item.branchCode || '-' }}</td>
            <td class="text-center text-caption">{{ formatDate(item.reserveDate) }}</td>
            <td class="text-center">
              <v-chip
                size="x-small"
                label
                variant="tonal"
                :color="item.withdrawnYn === 'Y' ? 'success' : 'warning'"
              >
                {{ withdrawnLabel(item.withdrawnYn) }}
              </v-chip>
            </td>
            <td class="text-center text-caption text-grey-darken-1">{{ formatDateTime(item.insertDt) }}</td>
          </tr>

          <tr v-if="!loading && items.length === 0">
            <td colspan="11" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">
                mdi-package-variant-closed
              </v-icon>
              검색 결과가 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <div class="px-4 pb-4">
        <AppPagination
          :page="page"
          :size="size"
          :total="total"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </v-card>
  </div>
</template>

<style scoped>
.withdrawal-row:hover {
  background-color: rgba(205, 178, 122, 0.06);
}
</style>
